import { useLoaderData } from 'react-router';
import { playerApi } from '@repo/api-client';
import { Player } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { PlusIcon, TrashIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import 'react-tooltip/dist/react-tooltip.css';
import { CreatePlayer } from '@/components/player/create-player';
import { useAsync } from '@repo/ui/hooks/use-async';
import { useRevalidator } from 'react-router';
import { useCurrentClub } from '@/hooks/use-current-club';
import { useState } from 'react';
import { DataTable } from '@repo/ui/components/data-table';
import { DataTableRowActions } from '@repo/ui/components/data-table-row-actions';
import { route } from './route';
import { InputTextWithClear } from '@repo/ui/components/input-text-with-clear';
import { useDebouncedQueryParam } from '@/hooks/use-debounced-query-param';

export function Players() {
  const club = useCurrentClub();
  const revalidator = useRevalidator();
  const { players } = useLoaderData<typeof route.loader>();
  const [open, setOpen] = useState(false);
  const { value: search, setValue: setSearch } = useDebouncedQueryParam('name');
  const { execute: deletePlayer } = useAsync(async (playerId: string) => {
    await playerApi.deletePlayer(club.id, playerId);
    revalidator.revalidate();
  });

  const createPlayerDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button>
          <PlusIcon className="w-4 h-4" />
          Crear Jugador
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Crear Jugador</DialogTitle>
        <DialogDescription>Completa los campos para crear un nuevo jugador.</DialogDescription>
        <CreatePlayer onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );

  const columns = [
    {
      header: 'Nombre',
      accessorKey: 'name',
    },
    {
      header: 'Apellido',
      accessorKey: 'lastName',
    },
    {
      header: 'Teléfono',
      accessorKey: 'phone',
    },
    {
      header: 'Acciones',
      accessorKey: 'actions',
      cell: ({ row }: { row: { original: Player } }) => (
        <DataTableRowActions
          actions={[
            {
              label: 'Eliminar',
              onClick: () => deletePlayer(row.original.id),
              icon: <TrashIcon className="w-4 h-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      total={players.count}
      columns={columns}
      data={players.results}
      filters={() => (
        <InputTextWithClear
          value={search}
          onChange={(value) => setSearch(value)}
          onClear={() => setSearch('')}
          placeholder="Buscar por nombre..."
        />
      )}
      mainActions={() => createPlayerDialog}
    />
  );
}
